import { z } from 'zod';
import {
  CurrencySchema,
  IsoDateSchema,
  TenantIdSchema,
  TrnSchema,
  VatRateSchema,
} from './primitives';

/**
 * Solara Invoice schema — input shape for the intake module (M2).
 * Source: Solara spec § Adatszótár 1.2 + 2 (auto-calc) + 3 (validation).
 *
 * Server-side fields (invoice_id, idempotency_key, intake_status,
 * received_at) are filled by the API layer and not part of the
 * client payload contract.
 */

export const LineItemSchema = z.object({
  description: z.string().min(1, { message: 'E007' }).max(500, { message: 'E007' }),
  descriptionAr: z.string().max(500).optional(),
  quantity: z
    .number()
    .positive({ message: 'E030' })
    .multipleOf(0.000001, { message: 'E030' }),
  unitPrice: z
    .number()
    .positive({ message: 'E031' })
    .multipleOf(0.01, { message: 'E031' }),
  vatRate: VatRateSchema,
});
export type LineItem = z.infer<typeof LineItemSchema>;

export const InvoiceInputSchema = z
  .object({
    tenantId: TenantIdSchema,
    invoiceNumber: z
      .string()
      .min(1, { message: 'E005' })
      .max(50, { message: 'E005' })
      .regex(/^[A-Za-z0-9_\-/]+$/u, { message: 'E005' }),
    sellerTrn: TrnSchema,
    buyerTrn: TrnSchema,
    buyerName: z.string().min(1).max(200),
    issueDate: IsoDateSchema,
    deliveryDate: IsoDateSchema.optional(),
    currency: CurrencySchema.default('AED' as never),
    lineItems: z.array(LineItemSchema).min(1, { message: 'E030-MIN' }),
  })
  .superRefine((value, ctx) => {
    if (value.deliveryDate && value.deliveryDate < value.issueDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'E003-D',
        path: ['deliveryDate'],
      });
    }
    const today = new Date().toISOString().slice(0, 10);
    if (value.issueDate > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'E002',
        path: ['issueDate'],
      });
    }
  });
export type InvoiceInput = z.infer<typeof InvoiceInputSchema>;

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface InvoiceTotals {
  totalNetAmount: number;
  totalVatAmount: number;
  totalGrossAmount: number;
  vatByRate: { rate: 5 | 0 | 'EXEMPT'; net: number; vat: number }[];
}

/** Solara spec § 2 — Automatikus számítások. Pure, deterministic. */
export function computeInvoiceTotals(items: LineItem[]): InvoiceTotals {
  const buckets = new Map<5 | 0 | 'EXEMPT', { net: number; vat: number }>();

  let totalNet = 0;
  let totalVat = 0;

  for (const item of items) {
    const lineNet = round2(item.quantity * item.unitPrice);
    const lineVat = item.vatRate === 5 ? round2(lineNet * 0.05) : 0;

    totalNet = round2(totalNet + lineNet);
    totalVat = round2(totalVat + lineVat);

    const bucket = buckets.get(item.vatRate) ?? { net: 0, vat: 0 };
    bucket.net = round2(bucket.net + lineNet);
    bucket.vat = round2(bucket.vat + lineVat);
    buckets.set(item.vatRate, bucket);
  }

  return {
    totalNetAmount: totalNet,
    totalVatAmount: totalVat,
    totalGrossAmount: round2(totalNet + totalVat),
    vatByRate: Array.from(buckets, ([rate, v]) => ({ rate, ...v })),
  };
}
